import React, { useEffect, useState, useCallback } from "react";
import SQLTableLoader from "./sqlTableLoader";
import { ChatInputData, commonIconImageStyle } from "../pages/chatPage";
import { mergeStyles } from "@fluentui/react";
import { TbSql } from "react-icons/tb";
import axios from "axios";
import config from "../config";
import HCChart from "./hcChart";
import { FiCopy, FiThumbsDown, FiThumbsUp, FiCheck } from "react-icons/fi";
import { IoChatbubbleOutline } from "react-icons/io5";

import {
  canThisBeAChart,
  formatSQL,
  getSqlCmdFromChatItem,
  shemaToUse,
} from "./hc/chatItemLookups";
import { useMediaQuery } from "react-responsive";
import ChatInput from "./chatInput";
import { getThemeColors, Theme } from "../theme/colors";

const inlineIcon = mergeStyles({
  display: "inline-block",
  verticalAlign: "middle",
  marginRight: "10px",
  cursor: "pointer",
});

const ChatItemDetails = ({
  username,
  chatId,
  chatItem,
  theme,
  noFeedback = false,
}: {
  username: string;
  chatId: string | undefined;
  chatItem: ChatInputData;
  theme: Theme;
  noFeedback?: boolean;
}) => {
  const [activeTab, setActiveTab] = useState("Response");
  const [sqlFormatted, setSqlFormatted] = useState(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState<any[]>([]);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [thumbsUpClicked, setThumbsUpClicked] = useState(false);
  const [thumbsDownClicked, setThumbsDownClicked] = useState(false);
  const [feedbackClicked, setFeedbackClicked] = useState(false);
  const [openFeedback, setOpenFeedback] = useState(false);
  const [localFeedback, setLocalFeedback] = useState(
    chatItem.feedback.feedback || ""
  );

  const parsedSql = getSqlCmdFromChatItem(chatItem);
  const hasSQL = parsedSql.length > 0;
  const hasChart = canThisBeAChart(chatItem);
  const schema = shemaToUse(chatItem);

  const isMobile = useMediaQuery({ maxWidth: 768 });

  const toggleTab = useCallback((tabName: string) => {
    setActiveTab((prevTab) => (prevTab === tabName ? "" : tabName));
  }, []);

  const currentTheme = getThemeColors(username, theme);

  const logoutButtonClass = mergeStyles({
    marginRight: "10px",
    borderRadius: "20px",
    padding: "5px 20px",
    cursor: "pointer",
    color: currentTheme.text,
    border: `1px solid ${currentTheme.chatBTNBackground}`,
    backgroundColor: currentTheme.chatBTNBackground,
    ":hover": {
      backgroundColor: currentTheme.chatBTNHover,
      border: `1px solid ${currentTheme.chatBTNHover}`,
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.6,
      backgroundColor: currentTheme.disabledBg,
      border: `1px solid ${currentTheme.disabledBg}`,
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setFetchError(null);
      try {
        const response = await axios.post(
          `${config.api.baseUrl}/${
            chatItem.response.sql_id === 0
              ? "sql/read_table"
              : "sql/stored_table"
          }`,
          {
            sql: chatItem.response.sql_id === 0 ? parsedSql : undefined,
            sql_id:
              chatItem.response.sql_id !== 0
                ? chatItem.response.sql_id
                : undefined,
            schema,
            username,
          }
        );
        const data =
          chatItem.response.sql_id === 0
            ? response.data.rows
            : JSON.parse(response.data.stored_table).rows;
        setTableData(data);
      } catch (error) {
        setFetchError("Failed to load table data.");
        console.error("Error fetching table data:", error);
        setTableData([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (hasSQL) {
      fetchData();
    }
  }, [parsedSql, chatItem.response.sql_id, hasSQL, schema, username]);

  useEffect(() => {
    setActiveTab("Response");
    setThumbsUpClicked(false);
    setThumbsDownClicked(false);
    setLocalFeedback(chatItem.feedback.feedback || "");
    setFeedbackClicked(!!chatItem.feedback.feedback);
    if (chatItem.feedback.thumbs === true) {
      setThumbsUpClicked(true);
    } else if (chatItem.feedback.thumbs === false) {
      setThumbsDownClicked(true);
    }
    if (chatItem.feedback.feedback?.length > 0) {
      setFeedbackClicked(true);
    }
  }, [chatItem]);

  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(chatItem.response.answer).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      },
      (err) => {
        console.error("Could not copy text: ", err);
      }
    );
  }, [chatItem.response.answer]);

  const handleThumbsUpClick = useCallback(async () => {
    if (!thumbsUpClicked) {
      try {
        if (!chatItem.question_id) {
          throw new Error("Chat ID is not defined.");
        }
        await axios.post(`${config.api.baseUrl}/chat/question_feedback`, {
          question_id: parseInt(chatItem.question_id, 10),
          username: username,
          thumbs: "TRUE",
        });

        setThumbsUpClicked(true);
        setThumbsDownClicked(false);
      } catch (error) {
        console.error("Error submitting thumbs up feedback:", error);
      }
    }
  }, [thumbsUpClicked, chatItem.question_id, username]);

  const handleThumbsDownClick = useCallback(async () => {
    if (!thumbsDownClicked) {
      try {
        if (!chatItem.question_id) {
          throw new Error("Chat ID is not defined.");
        }
        await axios.post(`${config.api.baseUrl}/chat/question_feedback`, {
          question_id: parseInt(chatItem.question_id, 10),
          username: username,
          thumbs: "FALSE",
        });

        setThumbsDownClicked(true);
        setThumbsUpClicked(false);
      } catch (error) {
        console.error("Error submitting thumbs down feedback:", error);
      }
    }
  }, [thumbsDownClicked, chatItem.question_id, username]);

  const handleFeedbackClick = useCallback(() => {
    setOpenFeedback((prev) => !prev);
  }, []);

  const handleSubmitFeedback = async (message: string) => {
    if (message.trim() === "") {
      alert("Feedback cannot be empty");
      return;
    }
    try {
      if (!chatItem.question_id) {
        throw new Error("Chat ID is not defined.");
      }
      await axios.post(`${config.api.baseUrl}/chat/question_feedback`, {
        question_id: parseInt(chatItem.question_id, 10),
        username: username,
        feedback: message,
      });
      // Update local state with the new feedback message
      setLocalFeedback(message);
      setFeedbackClicked(true);
      setOpenFeedback(false);
    } catch (error) {
      console.error("Error submitting feedback:", error);
    }
  };

  return (
    <div style={{ paddingBottom: "0px" }}>
      <div
        className={mergeStyles({
          display: isMobile ? "block" : "flex",
          alignItems: isMobile ? "initial" : "center",
          gap: isMobile ? "10px" : "0px",
          paddingBottom: "30px",
        })}
      >
        {hasSQL && (
          <>
            <button
              className={logoutButtonClass}
              onClick={() => toggleTab("SQL")}
            >
              Code
            </button>
            <button
              className={logoutButtonClass}
              onClick={() => toggleTab("Table")}
              disabled={tableData.length === 0}
            >
              Table
            </button>
            <button
              className={logoutButtonClass}
              onClick={() => toggleTab("Chart")}
              disabled={tableData.length === 0}
            >
              Chart
            </button>
          </>
        )}

        {/* Wrapper for the icons */}
        {!noFeedback && (
          <div
            className={mergeStyles({
              display: isMobile ? "block" : "flex",
              alignItems: "center",
              marginTop: isMobile ? "10px" : "0px",
              marginLeft: isMobile ? "5px" : "0px",
            })}
          >
            <div
              className={inlineIcon}
              onClick={copyToClipboard}
              onMouseOver={(e) => (e.currentTarget.title = "Copy to clipboard")}
            >
              {copied ? <FiCheck size={16} /> : <FiCopy size={16} />}
            </div>
            <FiThumbsUp
              className={inlineIcon}
              size={16}
              onClick={!thumbsUpClicked ? handleThumbsUpClick : undefined}
              style={{
                fill: thumbsUpClicked
                  ? theme === "dark"
                    ? "white"
                    : "black"
                  : "none",
                cursor: thumbsUpClicked ? "default" : "pointer",
              }}
            />
            <FiThumbsDown
              className={inlineIcon}
              size={16}
              onClick={!thumbsDownClicked ? handleThumbsDownClick : undefined}
              style={{
                fill: thumbsDownClicked
                  ? theme === "dark"
                    ? "white"
                    : "black"
                  : "none",
                cursor: thumbsDownClicked ? "default" : "pointer",
              }}
            />
            <IoChatbubbleOutline
              className={inlineIcon}
              size={16}
              onClick={handleFeedbackClick}
              style={{
                fill: feedbackClicked
                  ? theme === "dark"
                    ? "white"
                    : "black"
                  : "none",
                strokeWidth: 2,
              }}
            />
          </div>
        )}
      </div>

      {openFeedback && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            paddingTop: "0px",
            paddingBottom: "5px",
            gap: "10px",
          }}
        >
          {feedbackClicked ? (
            <div
              style={{
                backgroundColor: currentTheme.background,
                border: `1px solid ${currentTheme.border}`,
                color: currentTheme.text,
                borderRadius: "4px",
                padding: "10px",
                margin: "10px 0",
              }}
            >
              <p style={{ fontWeight: "bold", margin: "0 0 5px 0" }}>
                Provided feedback:
              </p>
              <p style={{ margin: 0 }}>{localFeedback}</p>
            </div>
          ) : (
            <ChatInput
              theme={theme}
              onMessageSubmit={handleSubmitFeedback}
              isDisabled={false}
              placeholderText="Provide feedback..."
              passedInWidth="50%"
              username={username}
            />
          )}
        </div>
      )}

      {activeTab === "SQL" && hasSQL && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            paddingTop: "5px",
            paddingBottom: "5px",
            gap: "10px",
          }}
        >
          <div
            onClick={() => setSqlFormatted(!sqlFormatted)}
            className={commonIconImageStyle}
            style={{ cursor: "pointer" }}
          >
            <TbSql size={24} />
          </div>{" "}
          {sqlFormatted ? (
            <span
              style={{
                wordBreak: "break-word",
                marginRight: "1rem",
              }}
            >
              {parsedSql}
            </span>
          ) : (
            <pre>
              <code>{formatSQL(parsedSql)}</code>
            </pre>
          )}
        </div>
      )}

      {activeTab === "Table" && hasSQL && (
        <SQLTableLoader
          theme={theme}
          isLoading={isLoading}
          error={fetchError}
          tableData={tableData}
        />
      )}

      {activeTab === "Chart" && hasSQL && (
        <HCChart
          isLoading={isLoading}
          error={fetchError}
          tableData={tableData}
          chartID={hasChart}
          theme={theme}
          username={username}
        />
      )}
    </div>
  );
};

export default ChatItemDetails;
